
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";

import { Box, Stack, Typography } from "@mui/material";

import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

const colors = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#5E8F72",
  gold: "#C99B4D",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  muted: "#617168",
  border: "rgba(16, 44, 36, 0.10)",
};

type PresidentLetterContent = {
  headlineLine1: string;
  headlineLine2: string;
  salutation: string;
  paragraphs: string[];
  signature: string;
};

const DEFAULT_PRESIDENT_LETTER: PresidentLetterContent = {
  headlineLine1: "Jun Lee 님께",
  headlineLine2: "드리는 감사의 말씀",
  salutation: "Jun Lee 님께,",
  paragraphs: [
    "네팔의 어려운 이웃들을 위해 따뜻한 마음과 정성을 모아 주신 데 깊이 감사드립니다.",
    "Jun Lee 님의 관심과 노력은 단순한 후원을 넘어, 어려움을 겪고 있는 네팔의 이웃들에게 희망과 용기를 전하는 뜻깊은 나눔이 되었습니다.",
    "이번 나눔을 위해 애써 주신 마음에 경복동문회를 대표하여 진심으로 감사드립니다. 앞으로도 따뜻한 연대와 나눔의 마음이 더 많은 이웃에게 전해지기를 바랍니다.",
    "함께해 주셔서 다시 한번 감사드립니다.",
  ],
  signature: "경복동문회 회장 드림",
};

/** Card 172 — 대성고등학교 촛불회 Impact Card */
const CARD_172_PRESIDENT_LETTER: PresidentLetterContent = {
  headlineLine1: "Jun Lee 님께",
  headlineLine2: "드리는 감사의 말씀",
  salutation: "Jun Lee 님께,",
  paragraphs: [
    "네팔의 어려운 이웃들을 위해 따뜻한 마음과 정성을 모아 주신 데 깊이 감사드립니다.",
    "Jun Lee 님의 관심과 노력은 단순한 후원을 넘어, 어려움을 겪고 있는 네팔의 이웃들에게 희망과 용기를 전하는 뜻깊은 나눔이 되었습니다.",
    "이번 나눔을 위해 애써 주신 마음에 경복동문회를 대표하여 진심으로 감사드립니다. 앞으로도 따뜻한 연대와 나눔의 마음이 더 많은 이웃에게 전해지기를 바랍니다.",
    "함께해 주셔서 다시 한번 감사드립니다",
  ],
  signature: "대성고등학교 촛불회 드림",
};

function resolvePresidentLetterContent(
  mainCardId?: number
): PresidentLetterContent {
  if (Number(mainCardId) === 172) {
    return CARD_172_PRESIDENT_LETTER;
  }

  return DEFAULT_PRESIDENT_LETTER;
}

type ImpactCardPresidentThankYouPageProps = {
  /** Fills one book subpage with internal scroll for the letter body. */
  variant?: "standalone" | "subpage";
  mainCardId?: number;
};

export default function ImpactCardPresidentThankYouPage({
  variant = "standalone",
  mainCardId,
}: ImpactCardPresidentThankYouPageProps) {
  const isSubpage = variant === "subpage";
  const letter = resolvePresidentLetterContent(mainCardId);

  return (
    <Box
      component="article"
      lang="ko"
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: isSubpage ? "none" : 760,
        mx: "auto",
        height: isSubpage ? "100%" : "auto",
        minHeight: isSubpage ? 0 : undefined,
        flex: isSubpage ? 1 : undefined,
        display: isSubpage ? "flex" : "block",
        flexDirection: isSubpage ? "column" : undefined,
        overflow: isSubpage ? "hidden" : "visible",
        pb: isSubpage ? 0 : { xs: 2, sm: 4 },
        px: isSubpage ? { xs: 2.25, sm: 3 } : 0,
        pt: isSubpage ? { xs: 1.5, sm: 2.25 } : 0,
        fontFamily: HOPE_SANS,
        overflowWrap: "anywhere",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          flexShrink: 0,
          px: isSubpage ? 0 : { xs: 1, sm: 3 },
          pt: isSubpage ? 0 : { xs: 1.5, sm: 3 },
          pb: { xs: 1.35, sm: 2 },
        }}
      >
        <Box
          sx={{
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 },
            mx: "auto",
            mb: 1.1,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            bgcolor: colors.mist,
            color: colors.forest,
          }}
        >
          <VolunteerActivismOutlinedIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
        </Box>

        <Typography
          sx={{
            mb: 0.85,
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.74rem", sm: "0.7rem" },
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: colors.green,
          }}
        >
          감사의 편지
        </Typography>

        <Typography
          component="h1"
          sx={{
            mx: "auto",
            maxWidth: 520,
            fontFamily: HOPE_SERIF,
            fontWeight: 700,
            fontSize: {
              xs: "1.58rem",
              sm: "2rem",
            },
            lineHeight: 1.4,
            letterSpacing: "-0.035em",
            wordBreak: "keep-all",
            color: colors.forestDeep,
          }}
        >
          {letter.headlineLine1}
          <Box component="br" />
          {letter.headlineLine2}
        </Typography>

        <Box
          sx={{
            width: 34,
            height: 2,
            bgcolor: colors.gold,
            mx: "auto",
            mt: 1.5,
            borderRadius: 999,
          }}
        />
      </Box>

      <Box
        data-impact-thank-inner-scroll={isSubpage ? "" : undefined}
        sx={{
          flex: isSubpage ? 1 : undefined,
          minHeight: isSubpage ? 0 : undefined,
          overflowY: isSubpage ? "auto" : "visible",
          overscrollBehaviorY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: colors.paper,
            border: `1px solid ${colors.border}`,
            borderRadius: {
              xs: "18px",
              sm: "24px",
            },
            boxShadow: "0 8px 32px rgba(16, 44, 36, 0.045)",
            px: {
              xs: 2,
              sm: 5,
              md: 6,
            },
            py: {
              xs: 2.25,
              sm: 5,
            },
          }}
        >
          <Stack
            spacing={{ xs: 2, sm: 2.75 }}
            sx={{
              "& .letter-paragraph": {
                fontFamily: HOPE_SANS,
                fontSize: {
                  xs: "0.96rem",
                  sm: "1rem",
                },
                lineHeight: {
                  xs: 1.85,
                  sm: 2,
                },
                color: colors.forestDeep,
                wordBreak: "keep-all",
                textAlign: "left",
              },
            }}
          >
            <Typography className="letter-paragraph" sx={{ fontWeight: 800 }}>
              {letter.salutation}
            </Typography>

            {letter.paragraphs.map((paragraph) => (
              <Typography
                key={paragraph}
                className="letter-paragraph"
              >
                {paragraph}
              </Typography>
            ))}

            <Box
              sx={{
                pt: { xs: 1.25, sm: 2 },
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Box
                sx={{
                  textAlign: "right",
                  borderTop: `1px solid ${colors.border}`,
                  pt: 1.5,
                  minWidth: 110,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: HOPE_SERIF,
                    fontSize: {
                      xs: "1.08rem",
                      sm: "1.2rem",
                    },
                    fontWeight: 700,
                    color: colors.forestDeep,
                  }}
                >
                  {letter.signature}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {!isSubpage ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              pt: { xs: 2.5, sm: 3.5 },
              color: colors.muted,
            }}
          >
            <Box sx={{ width: 22, height: 1, bgcolor: colors.border }} />
            <FavoriteRoundedIcon sx={{ fontSize: 15, color: colors.gold }} />
            <Box sx={{ width: 22, height: 1, bgcolor: colors.border }} />
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}
